import { RefObject, useCallback, useContext, useMemo } from "react"
import { XmppContext } from "@/stream/xmppClient"
import i18n from "@/lib/i18n/i18n"
import AdHocCommands, { DeliveryOrderMetadata } from "../api/adHocCommands"
import { captureError } from "@/ErrorHandlers"
import * as Sentry from "@sentry/react"
import { isStopPending, Route2g } from "@/reducers/routes2gSlice"
import { Element } from "@xmpp/xml"
import { DriverContext } from "../contexts/DriverContext"

export type UpsertRouteError = {
  error: boolean
  message?: string
}

const useDriverContext = (
  routeJid: string | undefined,
  route2g: Route2g | undefined,
  highlightDuplicateStopRef: RefObject<((stopId: string) => void) | null>,
): DriverContext => {
  const { client } = useContext(XmppContext)

  const upsertRoute2g = useCallback(
    async (
      route: Route2g,
      routeJid: string,
      options?: {
        useLatLon?: boolean
        sendRouteHandle?: boolean
        orderMetadata?: DeliveryOrderMetadata
      },
    ): Promise<Element> => {
      window.analytics.track("UpdateRoute2g")
      try {
        const result = await AdHocCommands.upsertRoute2g(
          client,
          routeJid,
          route,
          options,
        )
        if (
          "getChild" in result &&
          result.getChild("command")?.attrs.status !== "completed"
        ) {
          Sentry.captureMessage(
            `Error while setting driver destination: ${result.toString()}`,
          )
          window.analytics.track("FailedUpsertRouteStops")
        } else if ("error" in result) {
          const toaster = window.mapToaster ?? window.appToaster
          toaster.show({
            message: result.message ?? i18n.t("chat.driverRoute.updateFailed"),
            intent: "danger",
            timeout: 5000,
          })
          throw Error(result.message)
        }

        return result
      } catch (error) {
        captureError(error, {
          origin: "UserAction",
          extra: { message: "upsertRouteStops", jid: routeJid },
        })
        throw error
      }
    },
    [client],
  )

  const clearRoute2g = useCallback(
    async (routeJid: string): Promise<Element> => {
      window.analytics.track("ClearRoute2g")
      const emptyRoute: Route2g = { jid: routeJid, stops: [] }
      return upsertRoute2g(emptyRoute, routeJid)
    },
    [upsertRoute2g],
  )

  const addRouteStop = useCallback(
    async (
      address: string,
      routeJid: string,
      msgId: string,
    ): Promise<Element> => {
      const stop = { location: { formattedAddress: address }, msgId: msgId }
      const currentStops = route2g?.stops || []
      const stops = [...currentStops, stop]
      const index = currentStops.filter((stop) => isStopPending(stop)).length

      window.analytics.track("AddRouteStop", {
        source: "address_link",
        index: index,
        multi: index > 0,
        msgId: msgId,
      })
      try {
        const result = await AdHocCommands.upsertRoute2g(client, routeJid, {
          jid: routeJid,
          stops: stops,
        })

        if (
          "getChild" in result &&
          result.getChild("command")?.attrs.status !== "completed"
        ) {
          Sentry.captureMessage(
            `Error while setting driver destination: ${result.toString()}`,
          )
          window.analytics.track("FailedAddRouteStop")
        } else if ("error" in result) {
          const toaster = window.mapToaster ?? window.appToaster
          toaster.show({
            message: result.message ?? i18n.t("chat.driverRoute.updateFailed"),
            intent: "danger",
            timeout: 5000,
          })
          throw Error(result.message)
        }
        return result
      } catch (error) {
        captureError(error, {
          origin: "UserAction",
          extra: { message: "addRouteStop", jid: routeJid },
        })
        throw error
      }
    },
    [client, route2g],
  )

  return useMemo(
    () => ({
      upsertRoute2g,
      addRouteStop,
      routeJid,
      clearRoute2g,
      highlightDuplicateStop: highlightDuplicateStopRef,
    }),
    [
      addRouteStop,
      clearRoute2g,
      routeJid,
      highlightDuplicateStopRef,
      upsertRoute2g,
    ],
  )
}

export default useDriverContext
